import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { DIVISION_COLORS, divisionColorKey, divisionSwatchClass, type DivisionColor } from "@/lib/division-color";
import { useSession } from "@/lib/auth-client";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  FolderTree,
  Check,
  X,
  ShieldCheck,
  UserCog,
  AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/divisions")({
  component: DivisionsPage,
});

interface DivisionSupervisor {
  userId: string;
  name: string;
  email: string;
  inDivision: boolean;
}

interface DivAdmin {
  id: string;
  name: string;
  email: string;
}

interface Division {
  id: string;
  name: string;
  color?: string | null;
  parentId: string | null;
  createdAt: string;
  supervisors?: DivisionSupervisor[];
  divAdmins?: DivAdmin[];
}

interface OrgUser {
  id: string;
  name: string;
  email: string;
  role?: string;
}

/** Checkbox styling for the supervisor picker rows. */
function cnCheck(checked: boolean): string {
  return [
    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
    checked ? "bg-paris-red border-paris-red" : "border-border bg-transparent",
  ].join(" ");
}

function ColorPicker({
  value,
  onChange,
  autoLabel,
}: {
  value: DivisionColor | "";
  onChange: (c: DivisionColor | "") => void;
  autoLabel?: string;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Division colour">
      <span className="text-xs text-muted-foreground">Colour</span>
      {autoLabel && (
        <button
          type="button"
          role="radio"
          aria-checked={value === ""}
          onClick={() => onChange("")}
          className={cn("rounded-full border px-2 py-0.5 text-xs", value === "" ? "border-foreground font-medium" : "border-border text-muted-foreground")}
        >
          {autoLabel}
        </button>
      )}
      {DIVISION_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={c}
          title={c}
          onClick={() => onChange(c)}
          className={cn("h-6 w-6 rounded-full border-2", divisionSwatchClass(c), value === c ? "border-foreground" : "border-transparent")}
        />
      ))}
    </div>
  );
}

function DivisionsPage() {
  const { data: session } = useSession();
  const currentRole = session?.user?.role;
  const isSuperAdmin = currentRole === "admin";
  const isDivAdmin = currentRole === "div_admin";
  const canManageSupervisors = isSuperAdmin || isDivAdmin;
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [newColor, setNewColor] = useState<DivisionColor | "">("");
  const [editColor, setEditColor] = useState<DivisionColor | "">("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
  });

  const divisions = data?.data ?? [];

  function invalidateDivisionCaches() {
    queryClient.invalidateQueries({ queryKey: ["admin", "divisions"] });
    queryClient.invalidateQueries({ queryKey: ["divisions"] });
    queryClient.invalidateQueries({ queryKey: ["all-divisions"] });
  }

  const createMutation = useMutation({
    mutationFn: ({ name, color }: { name: string; color: DivisionColor | "" }) =>
      apiPost("/api/admin/divisions", { name, ...(color ? { color } : {}) }),
    onSuccess: () => {
      toast.success("Division created");
      setNewName("");
      setNewColor("");
      setShowCreate(false);
      invalidateDivisionCaches();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color: DivisionColor | "" }) =>
      apiPut(`/api/admin/divisions/${id}`, { name, ...(color ? { color } : {}) }),
    onSuccess: () => {
      toast.success("Division updated");
      setEditingId(null);
      invalidateDivisionCaches();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  async function commitDelete(id: string) {
    try {
      await apiDelete(`/api/admin/divisions/${id}`);
      toast.success("Division deleted");
      invalidateDivisionCaches();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete division");
    }
  }

  // --- Org users (for both supervisor picker and div_admin picker) ---
  const { data: usersData } = useQuery({
    queryKey: ["admin", "users-for-supervisors"],
    queryFn: () => apiGet<{ data: OrgUser[] }>("/api/users"),
  });
  const orgUsers = usersData?.data ?? [];
  const nonAdminUsers = orgUsers.filter((u) => u.role !== "admin");

  // --- Supervisor management ---
  const [supEditId, setSupEditId] = useState<string | null>(null);
  const [supSelected, setSupSelected] = useState<string[]>([]);

  const setSupervisorsMutation = useMutation({
    mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>
      apiPut(`/api/admin/divisions/${id}/supervisors`, { userIds }),
    onSuccess: () => {
      toast.success("Supervisors updated");
      setSupEditId(null);
      invalidateDivisionCaches();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startEditSupervisors(d: Division) {
    setSupEditId(d.id);
    setSupSelected((d.supervisors ?? []).map((s) => s.userId));
  }

  function toggleSup(userId: string) {
    setSupSelected((prev) =>
      prev.includes(userId) ? prev.filter((u) => u !== userId) : [...prev, userId]
    );
  }

  // --- Div admin management ---
  const [divAdminEditId, setDivAdminEditId] = useState<string | null>(null);
  const [divAdminSelected, setDivAdminSelected] = useState<string[]>([]);
  const [divAdminSearch, setDivAdminSearch] = useState("");

  const setDivAdminMutation = useMutation({
    mutationFn: ({ divisionId, userIds }: { divisionId: string; userIds: string[] }) =>
      apiPut(`/api/admin/divisions/${divisionId}/div-admin`, { userIds }),
    onSuccess: () => {
      toast.success("Division admins updated");
      setDivAdminEditId(null);
      setDivAdminSearch("");
      invalidateDivisionCaches();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function startAssignDivAdmin(d: Division) {
    setDivAdminEditId(d.id);
    setDivAdminSelected((d.divAdmins ?? []).map((a) => a.id));
    setDivAdminSearch("");
    if (supEditId === d.id) setSupEditId(null);
  }

  function toggleDivAdmin(userId: string) {
    setDivAdminSelected((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }

  const filteredDivAdminUsers = nonAdminUsers.filter(
    (u) =>
      !divAdminSearch ||
      u.name.toLowerCase().includes(divAdminSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(divAdminSearch.toLowerCase())
  );

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error("Name is required");
      return;
    }
    createMutation.mutate({ name: newName.trim(), color: newColor });
  }

  function handleSaveEdit() {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, name: editName.trim(), color: editColor });
    }
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  const missingAdminCount = divisions.filter((d) => !d.divAdmins || d.divAdmins.length === 0).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Divisions</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage organizational divisions and sub-divisions.</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setShowCreate(!showCreate);
            setNewName("");
          }}
        >
          {showCreate ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {showCreate ? "Cancel" : "Add Division"}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Divisions are how you group workers (e.g. <em>Plumbing</em>, <em>HVAC</em>, <em>Electrical</em>).
        Each worker can belong to one or more divisions.
      </p>

      {missingAdminCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-3 py-2 text-amber-700 dark:text-amber-400 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {missingAdminCount} division{missingAdminCount > 1 ? "s have" : " has"} no Division Admin assigned.
          </span>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleCreate} className="flex gap-2">
              <Input
                placeholder="Division name (e.g. Plumbing)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                disabled={createMutation.isPending}
                className="flex-1"
              />
              <Button type="submit" size="sm" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Create"
                )}
              </Button>
            </form>
            <ColorPicker value={newColor} onChange={setNewColor} autoLabel="Automatic" />
          </CardContent>
        </Card>
      )}

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : divisions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FolderTree className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">
              No divisions yet. Add one to start organizing your team.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {divisions.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-3">
                {editingId === d.id ? (
                  <div className="space-y-2">
                  <div className="flex gap-2 items-center">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit();
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      disabled={updateMutation.isPending}
                      className="flex-1 h-9"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-green-600"
                      onClick={handleSaveEdit}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-600"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <ColorPicker value={editColor} onChange={setEditColor} />
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={cn("h-3 w-3 rounded-full shrink-0", divisionSwatchClass(divisionColorKey(d.id, divisions)))} aria-hidden />
                      <span className="text-sm font-medium truncate">{d.name}</span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditingId(d.id);
                          setEditName(d.name);
                          setEditColor(divisionColorKey(d.id, divisions));
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(d.id, d.name)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Division Admin */}
                {editingId !== d.id && (
                  <div className="mt-3 pt-3 border-t border-border">
                    {divAdminEditId === d.id ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <UserCog className="h-3.5 w-3.5" /> Division Admins
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              className="h-7 px-2"
                              onClick={() =>
                                setDivAdminMutation.mutate({ divisionId: d.id, userIds: divAdminSelected })
                              }
                              disabled={setDivAdminMutation.isPending}
                            >
                              {setDivAdminMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => {
                                setDivAdminEditId(null);
                                setDivAdminSearch("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                        <Input
                          placeholder="Search users..."
                          value={divAdminSearch}
                          onChange={(e) => setDivAdminSearch(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border">
                          {filteredDivAdminUsers.length === 0 ? (
                            <p className="text-xs text-muted-foreground p-2">No users found.</p>
                          ) : (
                            filteredDivAdminUsers.map((u) => {
                              const checked = divAdminSelected.includes(u.id);
                              return (
                                <button
                                  key={u.id}
                                  type="button"
                                  onClick={() => toggleDivAdmin(u.id)}
                                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted/60"
                                >
                                  <span className={cnCheck(checked)}>
                                    {checked && <Check className="h-3 w-3 text-white" />}
                                  </span>
                                  <span className="text-sm truncate flex-1">{u.name}</span>
                                  <span className="text-[11px] text-muted-foreground truncate max-w-[45%]">
                                    {u.email}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                          <UserCog className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                          <span className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Admin</span>
                          {d.divAdmins && d.divAdmins.length > 0 ? (
                            d.divAdmins.map((a) => (
                              <span
                                key={a.id}
                                className="px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 truncate"
                              >
                                {a.name}
                              </span>
                            ))
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 italic">
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              No admin assigned
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 shrink-0 text-xs"
                          onClick={() => startAssignDivAdmin(d)}
                        >
                          {d.divAdmins && d.divAdmins.length > 0 ? "Manage" : "Assign"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Supervisors (isSupervisor flag — for approvals) */}
                {editingId !== d.id && divAdminEditId !== d.id && (
                  <div className="mt-3 pt-3 border-t border-border">
                    {supEditId === d.id ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5" /> Supervisors
                          </span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              className="h-7 px-2"
                              onClick={() =>
                                setSupervisorsMutation.mutate({ id: d.id, userIds: supSelected })
                              }
                              disabled={setSupervisorsMutation.isPending}
                            >
                              {setSupervisorsMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => setSupEditId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                        <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border">
                          {orgUsers.length === 0 ? (
                            <p className="text-xs text-muted-foreground p-2">No users found.</p>
                          ) : (
                            orgUsers.map((u) => {
                              const checked = supSelected.includes(u.id);
                              return (
                                <button
                                  key={u.id}
                                  type="button"
                                  onClick={() => toggleSup(u.id)}
                                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted/60"
                                >
                                  <span className={cnCheck(checked)}>
                                    {checked && <Check className="h-3 w-3 text-white" />}
                                  </span>
                                  <span className="text-sm truncate flex-1">{u.name}</span>
                                  <span className="text-[11px] text-muted-foreground truncate max-w-[45%]">
                                    {u.email}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Supervisor</span>
                            {d.supervisors && d.supervisors.length > 0 ? (
                              d.supervisors.map((s) => (
                                <span
                                  key={s.userId}
                                  className={`px-2 py-0.5 rounded-full text-xs border flex items-center gap-1 ${
                                    s.inDivision
                                      ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700"
                                      : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700"
                                  }`}
                                >
                                  {!s.inDivision && <AlertTriangle className="h-3 w-3 shrink-0" />}
                                  {s.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground italic">
                                No supervisors assigned
                              </span>
                            )}
                          </div>
                          {canManageSupervisors && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 shrink-0 text-xs"
                              onClick={() => startEditSupervisors(d)}
                            >
                              Manage
                            </Button>
                          )}
                        </div>
                        {/* Warning note when a supervisor is not a member of this division */}
                        {d.supervisors && d.supervisors.some((s) => !s.inDivision) && (
                          <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md px-2.5 py-1.5">
                            <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                            <span>
                              {d.supervisors.filter((s) => !s.inDivision).map((s) => s.name).join(", ")}
                              {d.supervisors.filter((s) => !s.inDivision).length === 1 ? " is" : " are"} not in this division.{" "}
                              {isSuperAdmin
                                ? "Add them to the division from the Users page."
                                : "Ask a Super Admin to add them to this division."}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Step 1 */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete division?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete <strong>"{deleteTarget?.name}"</strong>. This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteTarget) return;
                setDeleteConfirm(deleteTarget);
                setDeleteTarget(null);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Step 2 */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>"{deleteConfirm?.name}"</strong>. You'll have 10 seconds to undo after confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteConfirm) return;
                const { id, name } = deleteConfirm;
                setDeleteConfirm(null);
                deleteWithUndo(name, () => commitDelete(id));
              }}
            >
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
